import { INDIVIDUAL_ID_TYPES, IndividualIdType, usesCustomerIdentity, validateIndividualNumber, validatePassportExpiry } from '../../../utils/validation/individualIdentification';
import IndividualAffordabilityFields from './IndividualAffordabilityFields';
import React, { ReactNode, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import CountrySelect from '../../common/CountrySelect';
import SouthAfricanCitySelect from '../../common/SouthAfricanCitySelect';
import Select from 'react-select';
import { MaritalStatus } from '../../../services/schemas/loanServiceSchemas/maritalStatus';
import {marriageRegimes,maritalCategory,maritalStatusError} from '../individualMaritalStatus';
import {
  getSouthAfricanIdDateOfBirth,
  normalizeTaxPinOrIdNumber,
  validateSouthAfricanIdNumber
} from '../../../utils/validation/taxPinAndSouthAfricanId';

type EmploymentType = 'PERMANENT' | 'CONTRACT' | 'SELF_EMPLOYED' | 'UNEMPLOYED';

// https://www.gov.za/about-sa/south-africas-provinces
const southAfricanProvinces = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
  'Mpumalanga', 'Northern Cape', 'North West', 'Western Cape'
].map(value => ({ value, label: value }));

type IndividualForm = {
  individual: {
    fullNames: string;
    firstName: string;
    lastName: string;
    idNumber: string;
    idType?: IndividualIdType;
    passportExpiryDate?: string;
    maritalStatus: MaritalStatus | 'MARRIED';
    isPreviouslyDisadvantaged?: boolean;
    occupation: string;
    employer: string;
    employmentType: EmploymentType;
    dateOfBirth: string;
    incomeSource: string;
    monthlyNetIncome: number;
    monthlyLivingExpenses: number;
    address: {
      streetAddress: string;
      city: string;
      province: string;
      postalCode: string;
      country: string;
    };
    contactDetails: {
      primaryPhone: string;
      secondaryPhone?: string;
      email: string;
    };
    dependents: number;
  }
}

interface IndividualInfoStepProps {
  section?: 'identity' | 'income' | 'contact' | 'employment' | 'all';
  idReadOnly?: boolean;
  creditCheckPanel?: ReactNode;
  statementIqPanel?: ReactNode;
}

export default function IndividualInfoStep({ section = 'all', creditCheckPanel, statementIqPanel, idReadOnly = false }: IndividualInfoStepProps) {
  const { register, formState: { errors }, watch, control, trigger, setError, clearErrors, setValue } = useFormContext<IndividualForm>();
  const show = (part: 'identity' | 'income' | 'contact' | 'employment') => section === 'all' || section === part;
  const firstName = watch('individual.firstName');
  const lastName = watch('individual.lastName');
  const country = watch('individual.address.country');
  const isSouthAfrica = ['south africa', 'za', 'zaf'].includes(country?.trim().toLowerCase() || '');
  const city = watch('individual.address.city');
  const province = watch('individual.address.province');
  // Only an explicit choice in this form derives/locks province; restored strings stay editable.
  const [chosenPlace, setChosenPlace] = useState<{ city: string; province: string; country: string } | null>(null);
  const provinceFromCity = isSouthAfrica && chosenPlace?.city === city
    && chosenPlace?.province === province && chosenPlace?.country === country;
  useEffect(() => {
    setValue('individual.fullNames', [firstName?.trim(), lastName?.trim()].filter(Boolean).join(' '));
  }, [firstName, lastName, setValue]);
  const individualErrors = errors.individual || {};
  const employmentType = watch('individual.employmentType') as EmploymentType | undefined;
  const maritalStatus = watch('individual.maritalStatus');
  const idType = watch('individual.idType') || 'SA_ID';
  const passport = idType === 'PASSPORT';
  // Customer ID: historical client with no ID or passport on file - the customer number is used, nothing to type
  const customerId = usesCustomerIdentity(idType);
  const watchedIdNumber = watch('individual.idNumber') || '';
  const liveIdValidation = validateIndividualNumber(normalizeTaxPinOrIdNumber(watchedIdNumber), idType);
  const liveIdError = watchedIdNumber.trim().length > 0 && liveIdValidation !== true ? liveIdValidation : '';

  useEffect(() => {
    const normalizedId = normalizeTaxPinOrIdNumber(watchedIdNumber);
    if (passport || validateSouthAfricanIdNumber(normalizedId) !== true) return;

    const dateOfBirth = getSouthAfricanIdDateOfBirth(normalizedId);
    if (dateOfBirth) {
      setValue('individual.dateOfBirth', dateOfBirth, { shouldDirty: true, shouldValidate: true });
    }
  }, [passport, setValue, watchedIdNumber]);

  // Helper functions to check employment type
  const needsEmployer = employmentType && (employmentType === 'PERMANENT' || employmentType === 'CONTRACT');

  return (
    <div className={section === 'income' ? undefined : 'form-section'}>
      {section !== 'income' && <h6 className="form-section-title">{section === 'contact' ? 'Contact Details' : section === 'employment' ? 'Employment Details' : 'Personal Information'}</h6>}

      <div className="row g-3">
        {show('identity') && <>
        {/* Basic Personal Information */}
        <div className="col-12">
          <div className="form-section">
            <h6 className="form-section-title">Basic Information</h6>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="firstName">Given Names</label>
                  <input
                    type="text"
                    className={`form-control ${individualErrors.firstName ? 'is-invalid' : ''}`}
                    id="firstName"
                    placeholder="Enter your given names"
                    {...register('individual.firstName', {
                      required: 'Given names are required'
                    })}
                  />
                  {individualErrors.firstName && (
                    <div className="invalid-feedback">{individualErrors.firstName.message as string}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="lastName">Surname</label>
                  <input id="lastName" className="form-control"
                    {...register('individual.lastName', { required: 'Surname is required' })} />
                  {individualErrors.lastName && <div className="text-danger">{individualErrors.lastName.message}</div>}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="individual-id-type">Identification Type</label>
                  <select id="individual-id-type" className="form-select" disabled={idReadOnly}
                    value={idType} onChange={event => {
                      setValue('individual.idType', event.target.value as IndividualIdType, { shouldDirty: true });
                      setValue('individual.idNumber', '', { shouldDirty: true });
                      setValue('individual.passportExpiryDate', '', { shouldDirty: true });
                      setValue('individual.dateOfBirth', '', { shouldDirty: true });
                      clearErrors(['individual.idNumber', 'individual.passportExpiryDate']);
                    }}>
                    {INDIVIDUAL_ID_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="idNumber">{customerId ? 'Customer ID' : passport ? 'Passport Number' : 'ID Number'}</label>
                  <Controller
                    name="individual.idNumber"
                    control={control}
                    rules={{
                      validate: value => validateIndividualNumber(value, idType)
                    }}
                    render={({ field }) => (
                      <input
                        type="text"
                        className={`form-control ${(individualErrors.idNumber || liveIdError) ? 'is-invalid' : ''}`}
                        id="idNumber"
                        readOnly={idReadOnly || customerId}
                        placeholder={customerId ? 'Filled in automatically from the customer number'
                          : passport ? 'Enter your passport number' : 'Enter your 13-digit ID number'}
                        value={field.value ?? ''}
                        inputMode={passport ? 'text' : 'numeric'}
                        maxLength={passport ? 20 : 13}
                        onChange={(e) => {
                          const rawValue = e.target.value.slice(0, passport ? 20 : 13);
                          field.onChange(rawValue);
                          const trimmed = normalizeTaxPinOrIdNumber(rawValue);
                          if (!trimmed) {
                            clearErrors('individual.idNumber');
                          } else {
                            const validation = validateIndividualNumber(trimmed, idType);
                            if (validation === true) {
                              clearErrors('individual.idNumber');
                            } else {
                              setError('individual.idNumber', { type: 'manual', message: validation });
                            }
                          }
                        }}
                        onBlur={async (e) => {
                          const trimmed = normalizeTaxPinOrIdNumber(e.target.value);
                          field.onChange(trimmed);
                          field.onBlur();
                          const validation = validateIndividualNumber(trimmed, idType);
                          if (validation === true) {
                            clearErrors('individual.idNumber');
                          } else {
                            setError('individual.idNumber', { type: 'manual', message: validation });
                          }
                          await trigger('individual.idNumber');
                        }}
                        aria-invalid={!!individualErrors.idNumber}
                        aria-describedby={individualErrors.idNumber ? 'individual-id-error' : undefined}
                      />
                    )}
                  />
                  {idReadOnly && <p className="small text-muted mt-1 mb-0">Manage ID corrections from the customer profile. XDS-confirmed IDs stay locked.</p>}
                  {customerId && <p className="small text-muted mt-1 mb-0">Historical client with no ID or passport on file. The customer number identifies them, and they cannot be checked at the credit bureau.</p>}
                  {(individualErrors.idNumber || liveIdError) && (
                    <div id="individual-id-error" className="invalid-feedback d-block">
                      {liveIdError || (individualErrors.idNumber?.message as string)}
                    </div>
                  )}
                </div>
              </div>

              {passport && <div className="col-12 col-md-6">
                <label htmlFor="individual-passport-expiry">Passport Expiry Date</label>
                <input id="individual-passport-expiry" type="date" className="form-control"
                  {...register('individual.passportExpiryDate', { validate: value => watch('individual.idType') !== 'PASSPORT' || validatePassportExpiry(value) })} />
                {individualErrors.passportExpiryDate && <div role="alert" className="text-danger">{individualErrors.passportExpiryDate.message}</div>}
              </div>}
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="individual-date-of-birth">Date of Birth</label>
                  <input id="individual-date-of-birth" type="date" className="form-control"
                    {...register('individual.dateOfBirth', {required: 'Date of birth is required'})} />
                  {individualErrors.dateOfBirth && <div role="alert" className="text-danger">{individualErrors.dateOfBirth.message}</div>}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="dependents">Number of Dependents</label>
                  <input
                    type="number"
                    className={`form-control ${individualErrors.dependents ? 'is-invalid' : ''}`}
                    id="dependents"
                    min="0"
                    placeholder="Enter number of dependents"
                    {...register('individual.dependents', {
                      required: 'Number of dependents is required',
                      valueAsNumber: true,
                      min: {
                        value: 0,
                        message: 'Number of dependents cannot be negative'
                      }
                    })}
                  />
                  {individualErrors.dependents && (
                    <div className="invalid-feedback">{individualErrors.dependents.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="maritalStatus">Marital Status</label>
                  <input type="hidden" {...register('individual.maritalStatus', {required:'Marital status is required',
                    validate:value=>maritalStatusError(value) || true})} />
                  <select
                    className={`form-select ${individualErrors.maritalStatus ? 'is-invalid' : ''}`}
                    id="maritalStatus"
                    value={maritalCategory(maritalStatus)}
                    onChange={event=>{
                      clearErrors('individual.maritalStatus');
                      setValue('individual.maritalStatus',event.target.value as MaritalStatus | 'MARRIED',{shouldDirty:true});
                    }}
                  >
                    <option value="">Select marital status</option>
                    <option value="UNKNOWN">Unknown</option>
                    <option value="SINGLE">Single</option>
                    <option value="MARRIED">Married</option>
                    <option value="DIVORCED">Divorced</option>
                    <option value="WIDOWED">Widowed</option>
                  </select>
                  {individualErrors.maritalStatus && (
                    <div className="invalid-feedback">{individualErrors.maritalStatus.message as string}</div>
                  )}
                </div>
              </div>

              {maritalCategory(maritalStatus)==='MARRIED' && <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="marital-regime">Marital Regime</label>
                  <select id="marital-regime" className={`form-select ${individualErrors.maritalStatus ? 'is-invalid' : ''}`}
                      value={marriageRegimes.some(regime=>regime.value===maritalStatus) ? maritalStatus : ''}
                      onChange={event=>{
                        clearErrors('individual.maritalStatus');
                        setValue('individual.maritalStatus',(event.target.value || 'MARRIED') as MaritalStatus | 'MARRIED',{shouldDirty:true});
                      }}>
                      <option value="">Select marital regime</option>
                      {marriageRegimes.map(regime=><option key={regime.value} value={regime.value}>{regime.label}</option>)}
                  </select>
                </div>
              </div>}

              <div className="col-12">
                <fieldset className="form-group">
                  <legend className="float-none w-auto fs-6 mb-2">Previously disadvantaged individual</legend>
                  {[true, false].map(value => {
                    const label = value ? 'Yes' : 'No';
                    const id = `individual-isPreviouslyDisadvantaged-${label.toLowerCase()}`;
                    return <div key={label} className="form-check form-check-inline">
                      <input id={id} type="radio" className="form-check-input" name="individual.isPreviouslyDisadvantaged"
                        value={String(value)} checked={watch('individual.isPreviouslyDisadvantaged') === value}
                        onChange={() => setValue('individual.isPreviouslyDisadvantaged', value, { shouldDirty: true })} />
                      <label className="form-check-label" htmlFor={id}>{label}</label>
                    </div>;
                  })}
                </fieldset>
              </div>
            </div>
          </div>
        </div>

        </>}
        {show('contact') && <>
        {/* Contact Information */}
        <div className="col-12">
          <div className="form-section">
            <h6 className="form-section-title">Contact Information</h6>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="primaryPhone">Primary Phone Number</label>
                  <input
                    type="tel"
                    className={`form-control ${individualErrors.contactDetails?.primaryPhone ? 'is-invalid' : ''}`}
                    id="primaryPhone"
                    placeholder="0XXXXXXXXX"
                    inputMode="numeric"
                    maxLength={10}
                    {...register('individual.contactDetails.primaryPhone', { 
                      required: 'Primary phone number is required',
                      pattern: {
                        value: /^0\d{9}$/,
                        message: 'Please enter a valid South African 10-digit phone number'
                      }
                    })}
                  />
                  {individualErrors.contactDetails?.primaryPhone && (
                    <div className="invalid-feedback">{individualErrors.contactDetails.primaryPhone.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="secondaryPhone">Secondary Phone Number (Optional)</label>
                  <input
                    type="tel"
                    className={`form-control ${individualErrors.contactDetails?.secondaryPhone ? 'is-invalid' : ''}`}
                    id="secondaryPhone"
                    placeholder="0XXXXXXXXX"
                    inputMode="numeric"
                    maxLength={10}
                    {...register('individual.contactDetails.secondaryPhone', {
                      pattern: {
                        value: /^0\d{9}$/,
                        message: 'Please enter a valid South African 10-digit phone number'
                      }
                    })}
                  />
                  {individualErrors.contactDetails?.secondaryPhone && (
                    <div className="invalid-feedback">{individualErrors.contactDetails.secondaryPhone.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12">
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    className={`form-control ${individualErrors.contactDetails?.email ? 'is-invalid' : ''}`}
                    id="email"
                    placeholder="Enter your email address"
                    {...register('individual.contactDetails.email', { 
                      required: 'Email address is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address'
                      }
                    })}
                  />
                  {individualErrors.contactDetails?.email && (
                    <div className="invalid-feedback">{individualErrors.contactDetails.email.message as string}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="col-12">
          <div className="form-section">
            <h6 className="form-section-title">Residential Address</h6>
            <div className="row g-3">
              <div className="col-12">
                <label htmlFor="country">Country of Residence</label>
                <Controller name="individual.address.country" control={control}
                  rules={{ required: 'Country is required' }}
                  render={({ field }) => <CountrySelect id="country" value={field.value}
                    onChange={value => { setChosenPlace(null); field.onChange(value); }}
                    onBlur={field.onBlur} invalid={!!individualErrors.address?.country} />} />
                {individualErrors.address?.country && <div className="text-danger">{individualErrors.address.country.message}</div>}
              </div>
              <div className="col-12">
                <div className="form-group">
                  <label htmlFor="streetAddress">Street Address</label>
                  <input
                    type="text"
                    className={`form-control ${individualErrors.address?.streetAddress ? 'is-invalid' : ''}`}
                    id="streetAddress"
                    placeholder="Enter your street address"
                    {...register('individual.address.streetAddress', { 
                      required: 'Street address is required' 
                    })}
                  />
                  {individualErrors.address?.streetAddress && (
                    <div className="invalid-feedback">{individualErrors.address.streetAddress.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  {isSouthAfrica ? <Controller name="individual.address.city" control={control}
                    rules={{ required: 'City is required' }}
                    render={({ field }) => <SouthAfricanCitySelect key={country} id="city" value={field.value}
                      onChange={(name, matchedProvince) => {
                        setValue('individual.address.city', name, { shouldDirty: true, shouldValidate: true });
                        setChosenPlace(matchedProvince ? { city: name, province: matchedProvince, country } : null);
                        if (matchedProvince) setValue('individual.address.province', matchedProvince,
                          { shouldDirty: true, shouldValidate: true });
                      }}
                      onBlur={field.onBlur} invalid={!!individualErrors.address?.city} />} /> : <input
                    type="text"
                    className={`form-control ${individualErrors.address?.city ? 'is-invalid' : ''}`}
                    id="city"
                    placeholder="Enter your city"
                    {...register('individual.address.city', { 
                      required: 'City is required' 
                    })}
                  />}
                  {individualErrors.address?.city && (
                    <div className={isSouthAfrica ? 'text-danger' : 'invalid-feedback'}>{individualErrors.address.city.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="form-group">
                  <label htmlFor="province">Province</label>
                  {isSouthAfrica ? <Controller name="individual.address.province" control={control}
                    rules={{ required: 'Province is required' }}
                    render={({ field }) => <Select
                      inputId="province" instanceId="province" options={southAfricanProvinces}
                      value={southAfricanProvinces.find(option => option.value.toLowerCase() === field.value?.trim().toLowerCase())
                        || (field.value ? { value: field.value, label: field.value } : null)}
                      onChange={option => field.onChange(option?.value || '')} onBlur={field.onBlur}
                      isSearchable isClearable placeholder="Select a province…"
                      isDisabled={provinceFromCity}
                      aria-invalid={!!individualErrors.address?.province}
                      styles={{ control: (base, state) => ({ ...base, minHeight: 38,
                        borderColor: individualErrors.address?.province ? '#dc3545' : state.isFocused ? '#198754' : base.borderColor }) }}
                    />} /> : <input
                    type="text"
                    className={`form-control ${individualErrors.address?.province ? 'is-invalid' : ''}`}
                    id="province"
                    placeholder="Enter your province"
                    {...register('individual.address.province', { 
                      required: 'Province is required' 
                    })}
                  />}
                  {individualErrors.address?.province && (
                    <div className={isSouthAfrica ? 'text-danger' : 'invalid-feedback'}>{individualErrors.address.province.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="form-group">
                  <label htmlFor="postalCode">Postal Code</label>
                  <input
                    type="text"
                    className={`form-control ${individualErrors.address?.postalCode ? 'is-invalid' : ''}`}
                    id="postalCode"
                    placeholder="Enter your postal code"
                    {...register('individual.address.postalCode', { 
                      required: 'Postal code is required' 
                    })}
                  />
                  {individualErrors.address?.postalCode && (
                    <div className="invalid-feedback">{individualErrors.address.postalCode.message as string}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        </>}
        {show('employment') && <>
        {/* Employment Information */}
        <div className="col-12">
          <div className="form-section">
            <h6 className="form-section-title">Employment Information</h6>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="employmentType">Employment Type</label>
                  <select
                    className={`form-select ${individualErrors.employmentType ? 'is-invalid' : ''}`}
                    id="employmentType"
                    {...register('individual.employmentType', { 
                      required: 'Employment type is required',
                      onChange: () => {
                        setValue('individual.employer', '');
                        setValue('individual.incomeSource', '');
                      }
                    })}
                  >
                    <option value="">Select employment type</option>
                    <option value="PERMANENT">Permanent</option>
                    <option value="CONTRACT">Contract</option>
                    <option value="SELF_EMPLOYED">Self-Employed</option>
                    <option value="UNEMPLOYED">Unemployed — other income</option>
                  </select>
                  {individualErrors.employmentType && (
                    <div className="invalid-feedback">{individualErrors.employmentType.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="occupation">Occupation (if applicable)</label>
                  <input
                    type="text"
                    className={`form-control ${individualErrors.occupation ? 'is-invalid' : ''}`}
                    id="occupation"
                    placeholder="Enter your occupation"
                    disabled={employmentType === 'UNEMPLOYED'}
                    {...register('individual.occupation')}
                  />
                  {individualErrors.occupation && (
                    <div className="invalid-feedback">{individualErrors.occupation.message as string}</div>
                  )}
                </div>
              </div>

              {needsEmployer && (
                <div className="col-12">
                  <div className="form-group">
                    <label htmlFor="employer">Employer</label>
                    <input
                      type="text"
                      className={`form-control ${individualErrors.employer ? 'is-invalid' : ''}`}
                      id="employer"
                      placeholder="Enter your employer's name"
                      {...register('individual.employer', { 
                        required: needsEmployer ? 'Employer is required' : false
                      })}
                    />
                    {individualErrors.employer && (
                      <div className="invalid-feedback">{individualErrors.employer.message as string}</div>
                    )}
                  </div>
                </div>
              )}

              {!needsEmployer && <div className="col-12">
                <label htmlFor="individual-income-source">Income Source</label>
                <input id="individual-income-source" className="form-control" maxLength={255}
                  placeholder="Describe the source of regular income"
                  {...register('individual.incomeSource', {required: 'Income source is required'})} />
                {individualErrors.incomeSource && <div role="alert" className="text-danger">{individualErrors.incomeSource.message}</div>}
              </div>}
            </div>
          </div>
        </div>

        </>}
        {show('income') && <>
        <div className="col-12">
          <IndividualAffordabilityFields showHeading={section !== 'income'} />
        </div>
        </>}
        {show('identity') && creditCheckPanel && <div className="col-12">{creditCheckPanel}</div>}
        {(show('identity') || show('income')) && statementIqPanel && <div className="col-12">{statementIqPanel}</div>}
      </div>
    </div>
  );
} 
